import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Building2, Check, Copy, ExternalLink, Link2, Plus, RefreshCw, Search, ShieldOff, ShieldCheck, Users } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { CardDrawer } from './CRM';

const API = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');

export default function PartnerClients() {
  const { darkMode } = useSettings();
  const [partners, setPartners] = useState([]);
  const [clients, setClients] = useState([]);
  const [name, setName] = useState('');
  const [search, setSearch] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState('');
  const [clientAction, setClientAction] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [p, c] = await Promise.all([
        axios.get(`${API}/partner-clients/partners`),
        axios.get(`${API}/partner-clients/clients`)
      ]);
      setPartners(p.data.data || []);
      setClients(c.data.data || []);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Não foi possível carregar os clientes parceiros.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const publicLink = token => `${window.location.origin}/coleta-parceiro/${token}`;

  // LINK_OFICIAL_FINANCY_CARD
  const financyLink =
    `${window.location.origin}/coleta-financy`;

  async function copyFinancyLink() {
    try {
      await navigator.clipboard.writeText(
        financyLink
      );
    } catch (_error) {
      window.prompt(
        'Copie o link:',
        financyLink
      );
    }

    setCopied('financy');

    setTimeout(
      () => setCopied(null),
      1800
    );
  }

  async function createPartner(event) {
    event.preventDefault();
    if (name.trim().length < 2) return;
    try {
      setSaving(true);
      setMessage('');
      await axios.post(`${API}/partner-clients/partners`, { nome: name.trim() });
      setName('');
      setMessage('Parceiro criado. O link já pode ser copiado e enviado.');
      await load();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Erro ao criar parceiro.');
    } finally {
      setSaving(false);
    }
  }

  async function copyLink(partner) {
    const link = publicLink(partner.public_token);
    try {
      await navigator.clipboard.writeText(link);
    } catch (_error) {
      window.prompt('Copie o link:', link);
    }
    setCopied(partner.id);
    setTimeout(() => setCopied(null), 1800);
  }

  async function togglePartner(partner) {
    const action = partner.ativo ? 'desativar' : 'reativar';
    if (!window.confirm(`Deseja ${action} o link de ${partner.nome}?`)) return;
    try {
      await axios.patch(`${API}/partner-clients/partners/${partner.id}`, { ativo: !partner.ativo });
      await load();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Erro ao alterar o link.');
    }
  }

  async function regenerate(partner) {
    if (!window.confirm(`Gerar um novo link para ${partner.nome}? O link anterior deixará de funcionar imediatamente.`)) return;
    try {
      await axios.post(`${API}/partner-clients/partners/${partner.id}/regenerate-token`);
      setMessage('Novo link gerado. O link anterior foi invalidado.');
      await load();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Erro ao gerar novo link.');
    }
  }

  function awaitingPartnerPayment(client) {
    return (
      client.stage ===
        'AGUARDANDO_PAGAMENTO_PARCEIRO' &&
      client.pagamento_status ===
        'aguardando_parceiro'
    );
  }

  async function confirmPartnerPayment(client) {
    const valorInformado =
      window.prompt(
        `Valor pago pelo parceiro "${client.partner_name}" pelo serviço de "${client.nome}":\n\nExemplo: 850,00`
      );

    if (valorInformado === null) {
      return;
    }

    const valorNormalizado =
      String(valorInformado)
        .trim()
        .replace(/R\$/gi, '')
        .replace(/\s/g, '')
        .replace(/\./g, '')
        .replace(',', '.');

    const valor =
      Number(valorNormalizado);

    if (
      !Number.isFinite(valor) ||
      valor <= 0
    ) {
      window.alert(
        'Informe um valor válido. Exemplo: 850,00'
      );
      return;
    }

    const valorFormatado =
      valor.toLocaleString(
        'pt-BR',
        {
          style: 'currency',
          currency: 'BRL'
        }
      );

    const confirmar =
      window.confirm(
        `Confirmar pagamento?\n\nParceiro: ${client.partner_name}\nCliente: ${client.nome}\nValor pago: ${valorFormatado}\n\nAo clicar em OK, o pagamento será registrado e o cliente será liberado para a Esteira de Produção.`
      );

    if (!confirmar) {
      return;
    }

    const action =
      `payment-${client.id}`;

    try {
      setClientAction(action);
      setMessage('');

      const response =
        await axios.post(
          `${API}/partner-clients/clients/${client.id}/confirm-payment`,
          {
            valor_pago: valor
          }
        );

      setMessage(
        response.data?.message ||
        `Pagamento de ${valorFormatado} confirmado e cliente liberado para produção.`
      );

      if (
        selected?.id === client.id
      ) {
        setSelected(null);
      }

      await load();
    } catch (error) {
      setMessage(
        error.response?.data?.error ||
        'Não foi possível confirmar o pagamento.'
      );
    } finally {
      setClientAction(null);
    }
  }

  async function deleteUnpaidPartnerClient(client) {
    const confirmacao =
      window.prompt(
        `ATENÇÃO: esta exclusão é definitiva.\n\nCliente: ${client.nome}\nParceiro: ${client.partner_name}\n\nPara excluir o cliente e seus documentos, digite EXCLUIR:`
      );

    if (
      String(confirmacao || '')
        .trim()
        .toUpperCase() !==
      'EXCLUIR'
    ) {
      return;
    }

    const action =
      `delete-${client.id}`;

    try {
      setClientAction(action);
      setMessage('');

      const response =
        await axios.delete(
          `${API}/partner-clients/clients/${client.id}`
        );

      setMessage(
        response.data?.message ||
        'Cliente não pago excluído definitivamente.'
      );

      if (
        selected?.id === client.id
      ) {
        setSelected(null);
      }

      await load();
    } catch (error) {
      setMessage(
        error.response?.data?.error ||
        'Não foi possível excluir o cliente.'
      );
    } finally {
      setClientAction(null);
    }
  }

  async function deletePartner(partner) {
    const totalClientes =
      Number(
        partner.total_clientes ||
        0
      );

    if (totalClientes > 0) {
      setMessage(
        `O parceiro "${partner.nome}" possui ${totalClientes} cliente(s). Exclua individualmente os clientes não pagos ou desative o link para preservar o histórico.`
      );

      return;
    }

    const confirmacao =
      window.prompt(
        `ATENÇÃO: o parceiro "${partner.nome}" e seu link serão excluídos definitivamente.\n\nPara confirmar, digite EXCLUIR:`
      );

    if (
      String(confirmacao || '')
        .trim()
        .toUpperCase() !==
      'EXCLUIR'
    ) {
      return;
    }

    const action =
      `partner-delete-${partner.id}`;

    try {
      setClientAction(action);
      setMessage('');

      const response =
        await axios.delete(
          `${API}/partner-clients/partners/${partner.id}`
        );

      setMessage(
        response.data?.message ||
        'Parceiro excluído definitivamente.'
      );

      await load();
    } catch (error) {
      setMessage(
        error.response?.data?.error ||
        'Não foi possível excluir o parceiro.'
      );
    } finally {
      setClientAction(null);
    }
  }

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter(client => {
      const samePartner = !partnerFilter || String(client.partner_id) === partnerFilter;
      const searchable = `${client.nome || ''} ${client.phone || ''} ${client.cpf_cnpj || ''} ${client.partner_name || ''}`.toLowerCase();
      return samePartner && (!q || searchable.includes(q));
    });
  }, [clients, search, partnerFilter]);

  const box = darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const input = darkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900';

  return (
    <div className={darkMode ? 'text-white' : 'text-gray-900'}>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Building2 size={25} /> Clientes Parceiro</h1>
          <p className={`mt-1 ${muted}`}>Links de coleta direta e acompanhamento operacional dos clientes indicados.</p>
        </div>
        <div className={`px-4 py-3 rounded-xl border ${box}`}>
          <span className="font-bold">{clients.length}</span> clientes de parceiros
        </div>
      </div>

      {message && <div className="mb-5 rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm">{message}</div>}

      <section className={`rounded-xl border p-5 mb-6 ${box}`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Link2 size={20} className="text-blue-500" />

              <h2 className="font-bold text-lg">
                Link oficial Financy
              </h2>

              <span className="text-xs font-bold rounded-full px-2.5 py-1 bg-green-500/15 text-green-500">
                PERMANENTE
              </span>
            </div>

            <p className={`text-sm mt-1 ${muted}`}>
              Use este link para clientes diretos da Financy. Ele segue o fluxo de contrato, assinatura, pagamento e coleta de dados.
            </p>

            <div className={`mt-4 rounded-lg border px-3 py-3 text-sm break-all ${input}`}>
              {financyLink}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 lg:self-end">
            <button
              type="button"
              onClick={copyFinancyLink}
              className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 text-sm font-semibold flex items-center gap-2"
            >
              {copied === 'financy'
                ? <Check size={16} />
                : <Copy size={16} />
              }

              {copied === 'financy'
                ? 'Copiado'
                : 'Copiar link'
              }
            </button>

            <a
              href={financyLink}
              target="_blank"
              rel="noreferrer"
              className={`rounded-lg border px-4 py-3 text-sm font-semibold flex items-center gap-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}
            >
              <ExternalLink size={16} />
              Abrir
            </a>
          </div>
        </div>
      </section>

      <section className={`rounded-xl border p-5 mb-6 ${box}`}>
        <h2 className="font-bold text-lg mb-1">Criar link de coleta</h2>
        <p className={`text-sm mb-4 ${muted}`}>Use um link separado para cada parceiro ou escritório. O nome ficará registrado no cliente e na Esteira.</p>
        <form onSubmit={createPartner} className="flex flex-col sm:flex-row gap-3">
          <input
            value={name}
            onChange={event => setName(event.target.value)}
            maxLength={160}
            placeholder="Nome do parceiro ou escritório"
            className={`flex-1 rounded-lg border px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 ${input}`}
          />
          <button disabled={saving || name.trim().length < 2} className="rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold px-5 py-3 flex items-center justify-center gap-2">
            <Plus size={18} /> {saving ? 'Criando...' : 'Criar parceiro'}
          </button>
        </form>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-8">
        {partners.map(partner => (
          <article key={partner.id} className={`rounded-xl border p-5 ${box}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-bold">{partner.nome}</h3>
                <div className={`text-sm mt-1 ${muted}`}><Users size={14} className="inline mr-1" />{partner.total_clientes || 0} cliente(s)</div>
              </div>
              <span className={`text-xs font-bold rounded-full px-2.5 py-1 ${partner.ativo ? 'bg-green-500/15 text-green-500' : 'bg-red-500/15 text-red-500'}`}>
                {partner.ativo ? 'LINK ATIVO' : 'LINK DESATIVADO'}
              </span>
            </div>
            <div className={`mt-4 rounded-lg border px-3 py-2 text-xs break-all ${input}`}>{publicLink(partner.public_token)}</div>
            <div className="flex flex-wrap gap-2 mt-3">
              <button onClick={() => copyLink(partner)} disabled={!partner.ativo} className="rounded-lg bg-blue-600 disabled:opacity-40 text-white px-3 py-2 text-sm flex items-center gap-2">
                {copied === partner.id ? <Check size={16} /> : <Copy size={16} />} {copied === partner.id ? 'Copiado' : 'Copiar link'}
              </button>
              <a href={publicLink(partner.public_token)} target="_blank" rel="noreferrer" className={`rounded-lg border px-3 py-2 text-sm flex items-center gap-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                <ExternalLink size={16} /> Abrir
              </a>
              <button onClick={() => regenerate(partner)} className={`rounded-lg border px-3 py-2 text-sm flex items-center gap-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                <RefreshCw size={16} /> Novo link
              </button>
              <button
                type="button"
                onClick={() => togglePartner(partner)}
                className={`rounded-lg border px-3 py-2 text-sm flex items-center gap-2 ${partner.ativo ? 'border-red-500/50 text-red-500' : 'border-green-500/50 text-green-500'}`}
              >
                {partner.ativo ? <ShieldOff size={16} /> : <ShieldCheck size={16} />}
                {partner.ativo ? 'Desativar' : 'Reativar'}
              </button>

              <button
                type="button"
                onClick={() => deletePartner(partner)}
                disabled={
                  clientAction ===
                  `partner-delete-${partner.id}`
                }
                title={
                  Number(partner.total_clientes || 0) > 0
                    ? 'O parceiro possui clientes vinculados'
                    : 'Excluir parceiro definitivamente'
                }
                className="rounded-lg border border-red-600/60 text-red-600 hover:bg-red-500/10 disabled:opacity-50 px-3 py-2 text-sm font-semibold"
              >
                {clientAction ===
                  `partner-delete-${partner.id}`
                    ? 'Excluindo...'
                    : 'Excluir parceiro'}
              </button>
            </div>
          </article>
        ))}
        {!loading && partners.length === 0 && <div className={`rounded-xl border p-8 text-center ${box} ${muted}`}><Link2 className="mx-auto mb-2" />Nenhum parceiro criado.</div>}
      </section>

      <section>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
          <h2 className="text-xl font-bold">Clientes recebidos</h2>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative">
              <Search size={17} className={`absolute left-3 top-3 ${muted}`} />
              <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Nome, telefone, CPF/CNPJ..." className={`rounded-lg border pl-9 pr-3 py-2.5 outline-none ${input}`} />
            </div>
            <select value={partnerFilter} onChange={event => setPartnerFilter(event.target.value)} className={`rounded-lg border px-3 py-2.5 ${input}`}>
              <option value="">Todos os parceiros</option>
              {partners.map(partner => <option key={partner.id} value={partner.id}>{partner.nome}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredClients.map(client => {
            const aguardandoPagamento =
              awaitingPartnerPayment(client);

            const confirmando =
              clientAction ===
              `payment-${client.id}`;

            const excluindo =
              clientAction ===
              `delete-${client.id}`;

            return (
              <article
                key={client.id}
                className={`text-left rounded-xl border p-5 hover:border-blue-500 transition-colors ${box}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <strong>
                    {client.nome || 'Sem nome'}
                  </strong>

                  <div className="flex flex-col items-end gap-1.5">
                    <span className="rounded-full bg-violet-500/15 text-violet-500 text-xs font-bold px-2 py-1">
                      PARCEIRO
                    </span>

                    {aguardandoPagamento && (
                      <span className="rounded-full bg-amber-500/15 text-amber-500 text-[10px] font-bold px-2 py-1 text-center">
                        AGUARDANDO PAGAMENTO
                      </span>
                    )}

                    {!aguardandoPagamento && (
                      <span className="rounded-full bg-green-500/15 text-green-500 text-[10px] font-bold px-2 py-1">
                        SERVIÇO LIBERADO
                      </span>
                    )}
                  </div>
                </div>

                <div className={`text-sm mt-3 space-y-1 ${muted}`}>
                  <div>
                    Parceiro:{' '}
                    <span className={darkMode ? 'text-gray-200' : 'text-gray-700'}>
                      {client.partner_name}
                    </span>
                  </div>

                  <div>
                    Celular: {client.phone}
                  </div>

                  <div>
                    {client.tipo_pessoa}: {client.cpf_cnpj}
                  </div>

                  <div>
                    Etapa:{' '}
                    {aguardandoPagamento
                      ? 'Aguardando pagamento do parceiro'
                      : client.status_kanban || client.stage}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => setSelected(client)}
                    className="rounded-lg border border-blue-500/40 text-blue-500 px-3 py-2 text-sm font-semibold"
                  >
                    Abrir ficha operacional
                  </button>

                  {aguardandoPagamento && (
                    <>
                      <button
                        type="button"
                        disabled={
                          confirmando ||
                          excluindo
                        }
                        onClick={() =>
                          confirmPartnerPayment(client)
                        }
                        className="rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-3 py-2 text-sm font-semibold flex items-center gap-1.5"
                      >
                        <Check size={16} />

                        {confirmando
                          ? 'Confirmando...'
                          : 'Pagamento confirmado'}
                      </button>

                      <button
                        type="button"
                        disabled={
                          confirmando ||
                          excluindo
                        }
                        onClick={() =>
                          deleteUnpaidPartnerClient(client)
                        }
                        className="rounded-lg border border-red-500/50 text-red-500 hover:bg-red-500/10 disabled:opacity-50 px-3 py-2 text-sm font-semibold"
                      >
                        {excluindo
                          ? 'Excluindo...'
                          : 'Excluir somente cliente'}
                      </button>
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
        {!loading && filteredClients.length === 0 && <div className={`rounded-xl border p-10 text-center ${box} ${muted}`}>Nenhum cliente parceiro encontrado.</div>}
      </section>

      {selected && (
        <CardDrawer
          lead={selected}
          coluna={selected.status_kanban || 'DOCUMENTACAO_RECEBIDA'}
          onClose={() => { setSelected(null); load(); }}
          onColumnChange={load}
          onReset={() => {}}
          onDelete={() => {}}
        />
      )}
    </div>
  );
}
